import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../../../../../assets/icons/icon-warning';
import { EmptyStateBox } from '../../../../../../components';
import { BlindFeatureTypeInternal, ChannelTypeInternal } from '../../../../../../data-access/gql-types/graphql';
import { useBlindCalibrate } from '../../../../api/blind/blind.hooks';
import { useGateCalibrate } from '../../../../api/gate/gate.hooks';
import { useChannelsStateStore } from '../../../../store/channels-state';
import { ChannelCoverStateType, ChannelCoverType, ChannelGateStateType, ChannelType } from '../../../../types';
import { BlindManualCalibrationDialog } from '../../../channel-configuration/components/blind-calibration/manual-calibration-dialog';
import './index.scss';

type CalibrationInfoDetailsProps = {
  channel: ChannelType;
};

const CalibrationInfoDetails: React.FC<CalibrationInfoDetailsProps> = <
  T extends ChannelCoverStateType | ChannelGateStateType,
>({
  channel,
}) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const calibrationBlind = useBlindCalibrate();
  const calibrationGate = useGateCalibrate();
  const [showManualDialog, setShowManualDialog] = useState<boolean>(false);
  const state = useChannelsStateStore((s) => s.channelState[channel.id]) as T | null;
  const blindManualCalibration =
    channel.data.type === ChannelTypeInternal.Blind &&
    (channel.data as ChannelCoverType).supportedBlindFeatures.includes(BlindFeatureTypeInternal.ManualCalibration);

  const handleCalibrate = () => {
    if (channel) {
      const variables = { deviceId: channel.deviceId, channelId: channel.id };

      if (channel.data.type === ChannelTypeInternal.Blind) {
        if (blindManualCalibration) {
          setShowManualDialog(true);
        } else {
          calibrationBlind.mutate(variables);
        }
      } else if (channel.data.type === ChannelTypeInternal.Gate) {
        calibrationGate.mutate(variables);
      }
    }
  };

  return (
    <div className="calibration-window p-l-24 p-r-24 m-b-24">
      <EmptyStateBox
        header={tc('status.calibrate')}
        btnText={tc('status.calibrate')}
        content={
          state?.calibrateState?.progress
            ? `${t('calibrationProgress')} ${state?.calibrateState?.progress}%`
            : t('calibrationNeeded')
        }
        icon={<IconWarning />}
        {...(!state?.calibrateState?.progress ? { onClick: handleCalibrate } : {})}
      />
      <BlindManualCalibrationDialog
        channel={channel}
        showManualDialog={showManualDialog}
        setShowManualDialog={setShowManualDialog}
      />
    </div>
  );
};

export default CalibrationInfoDetails;
